/**
 * NEW High-Level Metrics Context - Clean Architecture
 * This replaces the legacy context with a cleaner, more maintainable approach
 */

import type React from "react";
import { createContext, useContext, useState, useCallback, useEffect } from "react";

import { useCurrentStateAndParams } from "@uirouter/react";

import type { ILegendItems } from "app/components/React/Legends/legendsTypes";
import type { granularityItem } from "app/components/widget/widget-utilities/time-granularity";
import { toggleItems } from "app/components/widget/widget-utilities/time-granularity";
import type { IDurationData } from "app/services/DurationService";
import DurationService, { BasicDurations } from "app/services/DurationService";
import { proNavigator } from "single-spa/react-app/ProNavigator";

import type { SingleMetricKey, SingleMetricConfiguration } from "./config/config";
import { getSingleMetricConfig, SINGLE_METRIC_KEYS } from "./config/config";
import { CHART_COLORS } from "./config/consts";

// =============================================================================
// TYPES
// =============================================================================

interface IParams {
    category?: string;
    country?: string;
    duration?: string;
    webSource?: string;
    comparedDuration?: string;
    selectedMetric?: string;
    timeGranularity?: string;
    includeSubdomains?: boolean;
    [key: string]: any;
}

// =============================================================================
// NEW CONTEXT INTERFACE - Clean & Simple
// =============================================================================

interface HighLevelMetricsContextValue {
    // Metric Configuration
    selectedMetric: SingleMetricConfiguration;
    selectedMetricId: SingleMetricKey;
    setSelectedMetricId: (metricId: SingleMetricKey) => void;

    // Time Configuration
    timeGranularity: granularityItem;
    setTimeGranularity: (granularity: granularityItem) => void;

    // Duration Data (for PngHeader and other components)
    durations: IDurationData;

    // Legend Management
    legendItems: ILegendItems;
    setLegendItems: (items: ILegendItems) => void;
    shouldRenderLegends: boolean;

    // Query Parameters
    params: IParams;
    queryParams: IParams;

    // Period Configuration
    isPeriodOverPeriod: boolean;

    // Debug Info
    debugInfo: {
        contextType: "new";
        selectedMetricSource: "config";
    };
}

// =============================================================================
// CONTEXT CREATION
// =============================================================================

const HighLevelMetricsContext = createContext<HighLevelMetricsContextValue | null>(null);

// =============================================================================
// CUSTOM HOOK
// =============================================================================

export const useHighLevelMetricsContext = (): HighLevelMetricsContextValue => {
    const context = useContext(HighLevelMetricsContext);

    if (!context) {
        throw new Error("useHighLevelMetricsContext must be used within a HighLevelMetricsProvider");
    }

    return context;
};

// =============================================================================
// PROVIDER COMPONENT
// =============================================================================

interface HighLevelMetricsProviderProps {
    children: React.ReactNode;
}

export const HighLevelMetricsProvider: React.FC<HighLevelMetricsProviderProps> = ({ children }) => {
    const { params: routeParams } = useCurrentStateAndParams();

    // Extract route parameters using the same pattern as the original wrapper
    const params: IParams = {
        category: routeParams?.category || "",
        country: routeParams?.country || "999",
        duration: routeParams?.duration || "",
        webSource: routeParams?.webSource || "Total",
        comparedDuration: routeParams?.comparedDuration || "",
        selectedMetric: routeParams?.selectedMetric || "",
        includeSubdomains: routeParams?.includeSubdomains ?? true,
    };

    const getInitialMetricId = (urlMetric: string): SingleMetricKey => {
        // First, check if it's already a valid new metric ID
        if (urlMetric && SINGLE_METRIC_KEYS.includes(urlMetric as SingleMetricKey)) {
            return urlMetric as SingleMetricKey;
        }

        // Try to map legacy URL param to new metric ID
        const metricMapping: Record<string, SingleMetricKey> = {
            AvgMonthVisits: "monthly-visits",
            EngagementUniqueUsers: "unique-visitors",
            AvgVisitDuration: "visit-duration",
            PagesPerSession: "pages-per-visit",
            BounceRate: "bounce-rate",
        };

        if (urlMetric && metricMapping[urlMetric]) {
            return metricMapping[urlMetric];
        }

        // Fallback to first available metric
        return SINGLE_METRIC_KEYS[0];
    };

    // State Management - Initialize with URL param directly
    const [selectedMetricId, setSelectedMetricId] = useState<SingleMetricKey>(() =>
        getInitialMetricId(params.selectedMetric),
    );
    const selectedMetric = getSingleMetricConfig(selectedMetricId);

    // Update selectedMetricId when URL params change (for navigation/back button)
    useEffect(() => {
        const newMetricId = getInitialMetricId(params.selectedMetric);
        if (newMetricId !== selectedMetricId) {
            setSelectedMetricId(newMetricId);
        }
    }, [params.selectedMetric, selectedMetricId]);

    // Time granularity setup
    const is28d = params.duration === BasicDurations.LAST_TWENTY_EIGHT_DAYS;
    const defaultTimeGranularity = is28d ? toggleItems.Daily : toggleItems.Monthly;
    const [timeGranularity, setTimeGranularity] = useState<granularityItem>(defaultTimeGranularity);

    // Period over period detection
    const isPeriodOverPeriod = Boolean(params.comparedDuration);

    let durations: IDurationData;
    try {
        durations = DurationService.getDurationData(params.duration, params.comparedDuration);
    } catch (error) {
        durations = DurationService.getDurationData("1m", "");
    }

    // Legend items setup (corrected colors to match legacy system)
    const [legendItems, setLegendItems] = useState<ILegendItems>([
        { name: "Desktop", color: CHART_COLORS.DESKTOP, visible: true, metric: "Desktop" },
        { name: "Mobile Web", color: CHART_COLORS.MOBILE_WEB, visible: true, metric: "MobileWeb" },
    ]);

    const shouldRenderLegends = params.webSource === "Total" && !isPeriodOverPeriod;

    // Query parameters with current state
    const queryParams: IParams = {
        ...params,
        timeGranularity: timeGranularity.value,
        selectedMetric: selectedMetricId,
    };

    // Enhanced setSelectedMetricId with URL update
    const handleSetSelectedMetricId = useCallback((metricId: SingleMetricKey) => {
        setSelectedMetricId(metricId);
        // Update URL parameters for bookmarking
        void proNavigator.updateParams({ selectedMetric: metricId });
    }, []);

    const value: HighLevelMetricsContextValue = {
        selectedMetric,
        selectedMetricId,
        setSelectedMetricId: handleSetSelectedMetricId,
        timeGranularity,
        setTimeGranularity,
        durations,
        legendItems,
        setLegendItems,
        shouldRenderLegends,
        params,
        queryParams,
        isPeriodOverPeriod,
        debugInfo: {
            contextType: "new",
            selectedMetricSource: "config",
        },
    };

    return <HighLevelMetricsContext.Provider value={value}>{children}</HighLevelMetricsContext.Provider>;
};
